// @ts-check

// @ts-ignore
define((require) => {
    "use strict";

    const Alignment = require("util/Alignment");

    /**
     * @typedef ContainerPositionProperties
     * @type {Object}
     * @property {"top"|"left"} startContainer
     * @property {"bottom"|"right"} endContainer
     */

    /** @type {ContainerPositionProperties} */
    const CONTAINER_RELATIVE_PROP_HORIZONTAL = {
        startContainer: "left",
        endContainer:   "right"
    };


    /** @type {ContainerPositionProperties} */
    const CONTAINER_RELATIVE_PROP_VERTICAL = {
        startContainer: "top",
        endContainer:   "bottom"
    };

    /**
     * All of the values are in pixels.
     *
     * @typedef ContainerSize
     * @type {Object}
     * @property {Number} right
     * @property {Number} left
     * @property {Number} top
     * @property {Number} bottom
     * @property {Number} verticalCenter
     * @property {Number} horizontalCenter
     */

    /**
     * @typedef PositioningWidgetFunction
     * @type {Object}
     * @property {Function} getWidgetSizeFunction           - This function returns the size of the widget.
     * @property {Function} getWidgetOriginOffsetFunction   - This function get the offset in pixels if the object is rotated.
     * @property {Function} alignWidgetToStartContainer     - This align the widget at the start of the container.
     * @property {Function} alignWidgetToEndContainer       - This align the widget at the end of the container.
     * @property {Function} getPositionInPrc                - Get the position of a widget in percentage.
     */

    /**
     * @typedef PositioningContainerData
     * @type {Object}
     * @property {Object[]} widgets                 - The widgets sorted by the closest according to the start of the container.
     * @property {Number} containerStartPosition    - The position in pixel of the start of the container.
     * @property {Number} containerEndPosition      - The position in pixel of the end of the container.
     */

    /** ;
     * @typedef PositioningData
     * @type {Object}
     * @property {Number} spacing                           - The space between each widgets.
     * @property {Boolean} isSpacedToTheContainer           - If the widgets are overlapping the containerSize.
     * @property {Number} firstPosition                     - The position of the first widget.
     * @property {"left"|"top"} relativePositionProperty    - Property used to modify the widget position, can only be "left" or "top",
     *                                                        depending on the type of distribution.
     * @property {PositioningWidgetFunction} widgetFunctions - The functions related to the widget.
     */

    /**
     * @param {Object} left    - The left widget
     * @param {Object} right   - The right wiget
     * @returns {Object}       The widget with the highest right position value
     */
    const compareRightBoundingBox = (left, right) => left.boundingBox.right > right.boundingBox.right ? left : right;


    /**
     * @param {Object} left    - The left widget
     * @param {Object} right   - The right wiget
     * @returns {Object}       The widget with the highest bottom position value
     */
    const compareBottomBoundingBox = (left, right) => left.boundingBox.bottom > right.boundingBox.bottom ? left : right;

    /**
     * Loop through the widget and check if the last widget is the one creating the containerEndSize boundary.
     *
     * In the case of overlapping widget the last widget isn't always the one creating the containerEndSize.
     * This function will check that and swap the widget if necessary, in order to keep the algorithm running
     * without special handling.
     *
     * The widgets in the containerData are already sorted.
     *
     * @param {(prev: Object,next: Object) => Object} compareFunction    - Function to compare the widgets
     * @param {Object[]} widgets            -
     *
     */
    function checkEndContainerWidgetAndSwap(compareFunction, widgets) {
        const lastIndex             = widgets.length - 1;
        const endContainerWidget    = widgets.reduce(compareFunction, widgets[0]);
        const lastWidget            = widgets[lastIndex];

        if (endContainerWidget !== lastWidget) {
            const indexOfLastWidget = widgets.indexOf(endContainerWidget);

            // We simply swap the widget creating the container size in order to keep the flow of the algo going.
            widgets[indexOfLastWidget]  = lastWidget;
            widgets[lastIndex]          = endContainerWidget;
        }
    }

    /**
     * TLDR: notOverlapping     : (ContainerSpace - WUsedSpace) / Wn.length + 1 = Spacing
     *       notAlignedToScene  : (ContainerSpace - WUsedSpace) / Wn.length - 1 = Spacing
     *       overlapping        : (ContainerSpace - W1 - Wn) - (WAverageSize) / Wn.length - 1 = Spacing
     *
     * Compute the spacing for each widget depending if it's aligned to the scene or not.
     * Also takes into account if the size of the widgets is greater than the space available in the scene.
     *
     * If the widgets are overlapping the container size then, the first widget and last widget
     * creates the container boundaries. The average size of the widget inside of the container is used,
     * in order to compute the spacing (The spacing will be negative).
     *
     * @param {PositioningContainerData} containerData      - The data of the container
     * @param {PositioningWidgetFunction} widgetFunctions   - The function to interact with the widgets.
     * @param {Boolean} isAlignedToScene                    - Distribute according to the scene or the widgets
     *
     * @returns {{spacing: Number, isSpacedToTheContainer: Boolean, firstPosition: Number}}
     */
    function computeSpacingForDistribution(containerData, {getWidgetSizeFunction}, isAlignedToScene) {
        const {widgets, containerStartPosition, containerEndPosition} = containerData;
        const widgetUsedSpace       = widgets
            .map(({boundingBox}) => getWidgetSizeFunction(boundingBox))
            .reduce((prev, next) => prev + next);
        const containerSize         = containerEndPosition - containerStartPosition;
        const availableSpace        = containerSize - widgetUsedSpace;
        const areWidgetsOverlapping = availableSpace < 0;
        const firstWidgetSize       = getWidgetSizeFunction(widgets[0].boundingBox);
        const lastWidgetSize        = getWidgetSizeFunction(widgets[widgets.length - 1].boundingBox);
        const averageWidgetSize     = (widgetUsedSpace - firstWidgetSize - lastWidgetSize) / (widgets.length - 1);

        // If the widgets are overlapping then we remove the first and last widget, in order to
        // to calculate the space available between this two widgets.
        const containerSpace = areWidgetsOverlapping ? containerSize - firstWidgetSize - lastWidgetSize : availableSpace;

        // We only want the spacing  before the first widget and after the last widget,
        // if we are distributing to the scene and the widgets aren't overlapping.
        const numberOfSpacing = !areWidgetsOverlapping && isAlignedToScene ? widgets.length + 1 : widgets.length - 1;

        // if the widgets are overlapping, then there's a negative spacing between the widgets.
        const spacing = areWidgetsOverlapping
            ? (containerSpace / numberOfSpacing) - averageWidgetSize
            : containerSpace / numberOfSpacing;
        let firstPosition = spacing;

        if (!isAlignedToScene) {
            firstPosition += containerStartPosition;

            if (!areWidgetsOverlapping) {
                firstPosition += firstWidgetSize;
            }
        }

        if (areWidgetsOverlapping) {
            firstPosition += firstWidgetSize;
        }

        return {
            spacing,
            isSpacedToTheContainer: areWidgetsOverlapping || !isAlignedToScene,
            firstPosition
        };
    }

    /**
     * @param {{height: number, width: number}} experienceSize  - The experience size
     * @param {ContainerSize} containerSize                     - The size of the container
     *
     * @returns {PositioningWidgetFunction}
     */
    function createWidgetFunctionsHorizontally(experienceSize, containerSize) {
        return {
            getWidgetSizeFunction: (boundingBox) => boundingBox.width,
            getWidgetOriginOffsetFunction: (boundingBox, rect) => rect.left - boundingBox.left,
            alignWidgetToStartContainer: (widget) => Alignment.alignLeft([widget], containerSize.left, experienceSize),
            alignWidgetToEndContainer: (widget) => Alignment.alignRight([widget], containerSize.right, experienceSize),
            getPositionInPrc: (position) => (position / experienceSize.width) * 100
        };
    }

    /**
     *
     * @param {Object[]} widgets                                - The selected widgets to distribute
     * @param {ContainerSize} containerSize                     - The size of the container
     * @param {{height: number, width: number}} experienceSize  - The experience size
     * @param {Boolean} isAlignedToScene                        - Distribute according to the scene or the widgets
     *
     * @export
     */
    function distributeHorizontally(widgets, containerSize, experienceSize, isAlignedToScene) {
        const widgetFunctions = createWidgetFunctionsHorizontally(experienceSize, containerSize);

        widgets.sort((a, b) => a.boundingBox.left - b.boundingBox.left);

        checkEndContainerWidgetAndSwap(compareRightBoundingBox, widgets);

        prepareDataForDistribution(
            widgets,
            containerSize,
            experienceSize,
            isAlignedToScene,
            widgetFunctions,
            CONTAINER_RELATIVE_PROP_HORIZONTAL
        );
    }

    /**
     * @param {{height: number, width: number}} experienceSize  - The experience size
     * @param {ContainerSize} containerSize                     - The size of the container
     *
     * @returns {PositioningWidgetFunction}
     */
    function createWidgetFunctionsVertically(experienceSize, containerSize) {
        return {
            getWidgetSizeFunction: (boundingBox) => boundingBox.height,
            getWidgetOriginOffsetFunction: (boundingBox, rect) => Math.abs(boundingBox.top - rect.top),
            alignWidgetToStartContainer: (widget) => Alignment.alignTop([widget], containerSize.top, experienceSize),
            alignWidgetToEndContainer: (widget) => Alignment.alignBottom([widget], containerSize.bottom, experienceSize),
            getPositionInPrc: (position) => (position / experienceSize.height) * 100
        };
    }


    /**
     *
     * @param {Object[]} widgets                                - The selected widgets to distribute
     * @param {ContainerSize} containerSize                     - The size of the container
     * @param {{height: number, width: number}} experienceSize  - The experience size
     * @param {Boolean} isAlignedToScene                        - Distribute according to the scene or the widgets, scene references the container.
     *
     * @export
     */
    function distributeVertically(widgets, containerSize, experienceSize, isAlignedToScene) {
        /** @type {PositioningWidgetFunction} */
        const widgetFunctions = createWidgetFunctionsVertically(experienceSize, containerSize);

        widgets.sort((a, b) => a.boundingBox.top - b.boundingBox.top);

        checkEndContainerWidgetAndSwap(compareBottomBoundingBox, widgets);

        prepareDataForDistribution(
            widgets,
            containerSize,
            experienceSize,
            isAlignedToScene,
            widgetFunctions,
            CONTAINER_RELATIVE_PROP_VERTICAL
        );
    }

    /**
     *
     * @param {Object[]} widgets                                    - The selected widgets to distribute
     * @param {ContainerSize} containerSize                         - The size of the container
     * @param {{height: number, width: number}} experienceSize      - The experience size
     * @param {Boolean} isAlignedToScene                            - Distribute according to the scene or the widgets
     * @param {PositioningWidgetFunction} widgetFunctions            - The functions associated with the widget
     * @param {ContainerPositionProperties} containerRelativeProp   - The properties that determine the position to use
     *                                                              for the container size.
    */
    function prepareDataForDistribution(widgets, containerSize, experienceSize, isAlignedToScene, widgetFunctions, containerRelativeProp) {
        const {startContainer, endContainer} = containerRelativeProp;
        const containerData = {
            containerStartPosition: containerSize[startContainer],
            containerEndPosition: containerSize[endContainer],
            widgets
        };
        const computedSpacing = computeSpacingForDistribution(containerData, widgetFunctions, isAlignedToScene);

        /** @type {PositioningData} */
        const positioningData = {
            widgetFunctions,
            relativePositionProperty: startContainer,
            firstPosition: computedSpacing.firstPosition,
            isSpacedToTheContainer: computedSpacing.isSpacedToTheContainer,
            spacing: computedSpacing.spacing
        };
        let startIndex  = 0;
        let endIndex    = containerData.widgets.length;

        if (positioningData.isSpacedToTheContainer || !isAlignedToScene) {
            const firstWidget   = containerData.widgets[0];
            const lastWidget    = containerData.widgets[containerData.widgets.length - 1];

            widgetFunctions.alignWidgetToStartContainer(firstWidget);
            widgetFunctions.alignWidgetToEndContainer(lastWidget);

            startIndex = 1;
            endIndex--;
        }

        distribute(positioningData, containerData, startIndex, endIndex);
    }

    /**
     * Distribute all of the object horizontally/vertically according to the scene or selection
     *
     * @param {PositioningData} positioningData         - The prepared data in order to distribute the widgets.
     * @param {PositioningContainerData} containerData  - The data related to the container.
     * @param {Number} startIndex                       - The start index.
     * @param {Number} endIndex                         - the end index.
     */
    function distribute(positioningData, containerData, startIndex, endIndex) {
        const {widgetFunctions}  = positioningData;
        let position            = positioningData.firstPosition;

        for (let index = startIndex; index < endIndex; index++) {
            const widget                        = containerData.widgets[index];
            const {model, boundingBox, rect}    = widget;
            const widgetSize                    = widgetFunctions.getWidgetSizeFunction(boundingBox);

            if (position < containerData.containerStartPosition) {
                widgetFunctions.alignWidgetToStartContainer(widget);
            } else if (widgetSize + position > containerData.containerEndPosition) {
                widgetFunctions.alignWidgetToEndContainer(widget);
            } else {
                const originOffset          = widgetFunctions.getWidgetOriginOffsetFunction(boundingBox, rect);
                const percentagePosition    = widgetFunctions.getPositionInPrc(position + originOffset);

                model.set(positioningData.relativePositionProperty, percentagePosition);
            }

            position += widgetSize + positioningData.spacing;
        }
    }

    return {
        distributeHorizontally,
        distributeVertically,
        computeSpacingForDistribution,
        distribute,
        createWidgetFunctionsHorizontally,
        createWidgetFunctionsVertically
    };
});



// WEBPACK FOOTER //
// ./src/editor/util/Distribution.js